// MicLevelRing state (UI-C19, EM-12). ACTIVE (`heard`) follows the SERVER's phrase-open signal for this seat
// (`serverHears`: a speech_start for this seat has arrived and the phrase has not yet closed - speech_end,
// its segment landing final, or segment_dropped; see room/model.ts's `speaking` phase and speakingSeats()) -
// never the local level. MAJLIS-116 (UI-C19 amended 2026-09-25): his transcribed phrases measured -35..-50
// dBFS RMS, well under MIC_HIGH_LEVEL's -22 dBFS, so a level-gated "heard" would never light for him; checking
// `serverHears` BEFORE the level keeps the ring lit at any level once the server has the phrase open. Only when
// NO phrase is open does the local level decide: below MIC_HIGH_LEVEL is `idle` (dashed); at or above it starts
// the MIC_UNHEARD_MS timer toward "we can't hear you" (`unheard`), `hearing` until then. MIC_HIGH_LEVEL is used
// for NOTHING else - not a gate on `heard`, only the idle/hearing boundary and the unheard trigger. The level
// still drives the glow's SIZE regardless of ring state (BottomBar's --lvl), unchanged here.
// Muted, permission denied, no microphone and reconnecting are their own visible states, and so (MAJLIS-71,
// D-52) is `stopped`: the microphone's track ENDED and no re-open has succeeded yet. Priority: denied >
// unavailable > muted > stopped > reconnecting > playing > heard > the level. While the room's writing is
// paused (UI-C20: the engine is down, so no speech_start can come) the ring still follows the level but never
// says "we can't hear you"; when writing comes back the timer starts fresh: a loud stretch that began during
// the outage does not count.
// While THIS device is playing a phrase the microphone is gated (EM-25, micGate.ts): the ring says `playing`,
// so he can see why it went quiet and does not think it broke. His own mute outranks it - a muted seat reads
// `muted` whatever is playing - and the "we can't hear you" timer cannot run while nothing is being sent.
import { MIC_HIGH_LEVEL, MIC_UNHEARD_MS } from "../constants";

export type MicPermission = "pending" | "granted" | "denied" | "unavailable";

export type RingState =
  | "muted"
  | "denied"
  | "unavailable"
  | "stopped"
  | "reconnecting"
  | "playing"
  | "idle"
  | "hearing"
  | "heard"
  | "unheard";

export interface RingInput {
  muted: boolean;
  permission: MicPermission;
  /** MAJLIS-71 (D-52): the microphone's track ended and no re-open has succeeded yet (below his own mute). */
  stopped?: boolean;
  reconnecting: boolean;
  level: number; // local RMS, 0..1
  serverHears: boolean; // a phrase for this seat is open: speech_start seen, not yet closed (MAJLIS-116)
  engineDown: boolean; // UI-C20: the room's writing is paused (EM-12 warning suppressed)
  playing: boolean; // EM-25: this device is playing a phrase, so its microphone is gated
  now: number; // injected clock, ms
}

export class MicRing {
  private highSince: number | null = null;

  update(i: RingInput): RingState {
    if (i.permission === "denied") return this.reset("denied");
    if (i.permission === "unavailable") return this.reset("unavailable");
    if (i.muted) return this.reset("muted");
    if (i.stopped) return this.reset("stopped");
    if (i.reconnecting) return this.reset("reconnecting");
    if (i.playing) return this.reset("playing");
    if (i.serverHears) return this.reset("heard");
    if (i.level < MIC_HIGH_LEVEL) return this.reset("idle");
    if (i.engineDown) return this.reset("hearing");
    if (this.highSince === null) this.highSince = i.now;
    return i.now - this.highSince >= MIC_UNHEARD_MS ? "unheard" : "hearing";
  }

  private reset(s: RingState): RingState {
    this.highSince = null;
    return s;
  }
}
